import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Paperclip } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { ErrorBanner, Field, Textarea } from '@/components/form';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ApiError, api } from '@/lib/api';
import { useOrgId } from '@/lib/org';
import { pdfAction } from '@/lib/pdf';
import { KIND_INFO, type EmailDraft, type SalesDocument, type SentEmail } from '@/lib/sales';

/** Send: email the document's PDF to the customer (prefilled from Company settings › Sales). */
export function SendDialog({
  doc,
  open,
  onClose,
}: {
  doc: SalesDocument;
  open: boolean;
  onClose: () => void;
}) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const what = `${KIND_INFO[doc.kind].label} ${doc.number ?? ''}`.trim();
  const draft = useQuery({
    queryKey: ['sales', orgId, 'email-draft', doc.id],
    queryFn: () => api<EmailDraft>(`/orgs/${orgId}/sales/documents/${doc.id}/email`),
    enabled: open && Boolean(orgId),
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  // prefill once per opening, so a background refetch never overwrites what was typed
  const filled = useRef(false);
  useEffect(() => {
    if (!open) {
      filled.current = false;
      return;
    }
    if (!draft.data || filled.current) return;
    filled.current = true;
    setTo(draft.data.to);
    setCc(draft.data.cc);
    setSubject(draft.data.subject);
    setBody(draft.data.body);
  }, [open, draft.data]);
  const m = useMutation({
    mutationFn: () =>
      api<SentEmail>(`/orgs/${orgId}/sales/documents/${doc.id}/send`, {
        method: 'POST',
        body: JSON.stringify({ to, cc, subject, body }),
      }),
    onSuccess: async (r) => {
      await qc.invalidateQueries();
      const link = { to: '/settings/sent-emails', label: 'Sent emails' };
      if (r.status === 'failed')
        toast(`${what} could not be delivered: ${r.error ?? 'unknown error'}.`, {
          tone: 'error',
          link,
        });
      else
        toast(
          r.status === 'recorded'
            ? `${what} recorded for ${r.to}. This server does not deliver email.`
            : `${what} sent to ${r.to}.`,
          { link },
        );
      onClose();
    },
  });
  const fileName = draft.data?.attachmentName ?? `${what}.pdf`;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl" data-testid="send-dialog">
        <DialogHeader>
          <DialogTitle>Send {what}</DialogTitle>
          <DialogDescription>
            Separate several addresses with commas.
            {draft.data && !draft.data.delivers
              ? ' This server records the email on Sent emails instead of delivering it.'
              : ''}
          </DialogDescription>
        </DialogHeader>
        {m.error || draft.error ? (
          <ErrorBanner>
            {(m.error ?? draft.error) instanceof ApiError
              ? (m.error ?? draft.error)!.message
              : String(m.error ?? draft.error)}
          </ErrorBanner>
        ) : null}
        <Field label="To *">
          {(id) => (
            <Input
              id={id}
              type="text"
              inputMode="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          )}
        </Field>
        <Field label="Cc">
          {(id) => (
            <Input
              id={id}
              type="text"
              inputMode="email"
              value={cc}
              onChange={(e) => setCc(e.target.value)}
            />
          )}
        </Field>
        <Field label="Subject *">
          {(id) => (
            <Input
              id={id}
              value={subject}
              maxLength={300}
              onChange={(e) => setSubject(e.target.value)}
            />
          )}
        </Field>
        <Field label="Message">
          {(id) => (
            <Textarea
              id={id}
              className="min-h-40"
              value={body}
              maxLength={5000}
              onChange={(e) => setBody(e.target.value)}
            />
          )}
        </Field>
        <div className="flex items-center gap-2 text-sm">
          <Paperclip className="size-4 text-muted-foreground" aria-hidden />
          <button
            type="button"
            className="text-primary hover:underline"
            onClick={() =>
              void pdfAction('preview', `/orgs/${orgId}/sales/documents/${doc.id}/pdf`, fileName)
            }
          >
            {fileName}
          </button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={m.isPending || draft.isPending || !to.trim() || !subject.trim()}
            onClick={() => m.mutate()}
          >
            {m.isPending ? 'Sending…' : 'Send'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
